import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

const { FiX, FiUsers, FiMail, FiCheckCircle, FiClock, FiDownload } = FiIcons;
const { FiHeart, FiChevronDown } = FiIcons;

const ACCESS_OPTIONS = [
  'Wheelchair access',
  'Hearing loop',
  'Sign language interpreter',
  'Large print program',
  'Quiet space',
  'Dietary needs',
  'Reserved seating',
  'Transport assistance'
];

const RSVPManagerModal = ({ isOpen, onClose }) => {
  const [guests] = useState([
    { id: 1, name: 'Sarah Jenkins', email: 'sarah@example.com', status: 'Attending', guests: 2 },
    { id: 2, name: 'David Thompson', email: 'david@example.com', status: 'Attending', guests: 1 },
    { id: 3, name: 'Michael Reed', email: 'm.reed@example.com', status: 'Pending', guests: 0 }
  ]);
  const [access, setAccess] = useState({
    1: { needs: ['Wheelchair access'], note: '' },
    2: { needs: ['Hearing loop', 'Reserved seating'], note: 'Prefers front row, left side.' },
    3: { needs: [], note: '' }
  });
  const [openId, setOpenId] = useState(null);

  if (!isOpen) return null;

  const getAccess = (id) => access[id] || { needs: [], note: '' };

  const toggleNeed = (id, need) => {
    const cur = getAccess(id);
    const needs = cur.needs.includes(need) ? cur.needs.filter(n => n !== need) : [...cur.needs, need];
    setAccess(prev => ({ ...prev, [id]: { ...cur, needs } }));
  };

  const setNote = (id, note) => {
    setAccess(prev => ({ ...prev, [id]: { ...getAccess(id), note } }));
  };

  const needsCount = guests.filter(g => getAccess(g.id).needs.length > 0).length;

  return (
    <div className="fixed inset-0 z-[160] flex items-center justify-center p-4 bg-accent-navy/60 backdrop-blur-md">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="bg-white rounded-[40px] shadow-2xl w-full max-w-2xl overflow-hidden border border-brand-dark"
      >
        <div className="p-8 md:p-12 border-b border-brand-light flex justify-between items-center bg-brand-light/30">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-accent-navy text-white rounded-2xl flex items-center justify-center">
              <SafeIcon icon={FiUsers} className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-2xl font-serif text-accent-navy">RSVP Manager</h3>
              <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">Track memorial attendance</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-brand-light rounded-full text-text-muted">
            <SafeIcon icon={FiX} className="w-6 h-6" />
          </button>
        </div>

        <div className="p-8">
          <div className="flex justify-between items-center mb-8">
            <div className="flex gap-6">
              <div className="text-center">
                <p className="text-2xl font-serif text-accent-navy">{guests.filter(g => g.status === 'Attending').length}</p>
                <p className="text-[9px] font-black uppercase text-text-muted">Confirmed</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-serif text-text-muted">{guests.filter(g => g.status === 'Pending').length}</p>
                <p className="text-[9px] font-black uppercase text-text-muted">Pending</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-serif text-accent-gold">{needsCount}</p>
                <p className="text-[9px] font-black uppercase text-text-muted">Access Needs</p>
              </div>
            </div>
            <button className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-accent-gold hover:text-accent-navy">
              <SafeIcon icon={FiDownload} /> Export List
            </button>
          </div>

          <div className="space-y-3">
            {guests.map((guest) => (
              <div key={guest.id} className="flex items-center justify-between p-5 bg-white border border-brand-dark rounded-3xl">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-brand-light rounded-xl flex items-center justify-center text-accent-navy font-bold uppercase">
                    {guest.name[0]}
                  </div>
                  <div>
                    <h5 className="text-sm font-bold text-accent-navy">{guest.name}</h5>
                    <p className="text-[10px] text-text-muted font-medium">{guest.email}</p>
                  </div>
                </div>
                <div className="text-right">
                  <span className={`text-[9px] font-black uppercase tracking-widest px-3 py-1 rounded-full ${guest.status === 'Attending' ? 'bg-green-50 text-green-600 border border-green-100' : 'bg-brand-light text-text-muted'}`}>
                    {guest.status}
                  </span>
                  <p className="text-[10px] text-text-muted mt-1">{guest.guests} Guests</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 pt-8 border-t border-brand-light">
            <h4 className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-accent-navy mb-4">
              <SafeIcon icon={FiHeart} className="w-3 h-3 text-accent-gold" /> Access Needs
            </h4>
            <div className="space-y-3">
              {guests.map((guest) => {
                const g = getAccess(guest.id);
                return (
                  <div key={guest.id} className="border border-brand-dark rounded-3xl overflow-hidden">
                    <button
                      onClick={() => setOpenId(openId === guest.id ? null : guest.id)}
                      className="w-full flex items-center justify-between px-5 py-3 bg-brand-light/40 text-left"
                    >
                      <span className="text-xs font-bold text-accent-navy">
                        {guest.name}
                        <span className="ml-2 text-[10px] font-medium text-text-muted">
                          {g.needs.length > 0 ? `${g.needs.length} need${g.needs.length > 1 ? 's' : ''}` : 'None'}
                        </span>
                      </span>
                      <SafeIcon icon={FiChevronDown} className={`w-4 h-4 text-text-muted transition-transform ${openId === guest.id ? 'rotate-180' : ''}`} />
                    </button>
                    {openId === guest.id && (
                      <div className="p-5 space-y-4">
                        <div className="flex flex-wrap gap-2">
                          {ACCESS_OPTIONS.map(opt => {
                            const active = g.needs.includes(opt);
                            return (
                              <button
                                key={opt}
                                onClick={() => toggleNeed(guest.id, opt)}
                                className={`text-[10px] font-bold px-3 py-1.5 rounded-full border transition-colors ${active ? 'bg-accent-navy text-white border-accent-navy' : 'bg-white text-text-muted border-brand-dark hover:border-accent-gold'}`}
                              >
                                {opt}
                              </button>
                            );
                          })}
                        </div>
                        <textarea
                          value={g.note}
                          onChange={(e) => setNote(guest.id, e.target.value)}
                          placeholder="Additional notes for the host..."
                          rows={2}
                          className="w-full text-xs p-3 rounded-2xl border border-brand-dark focus:outline-none focus:border-accent-gold"
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default RSVPManagerModal;